import type { ReactElement } from 'react';

import { Alert } from '@flintmint/web-shared/components/alert';

type ArchiveAction = 'restore' | 'delete';

const getArchiveActionConfig = (action: ArchiveAction, count: number) => {
  const isSingle = count === 1;
  const itemLabel = isSingle ? 'item' : 'items';

  if (action === 'restore') {
    return {
      type: 'info' as const,
      color: 'hero' as const,
      icon: 'alert-01-bulk' as const,
      title: `Restore selected ${itemLabel}?`,
      message: `Selected ${itemLabel} will return to their previous locations.`,
      confirmLabel: isSingle ? 'Restore item' : 'Restore items',
      confirmColor: 'hero' as const,
    };
  }

  return {
    type: 'danger' as const,
    title: 'Delete from Archive',
    message: `Selected ${itemLabel} will be removed from Archive and will be permanently deleted. This action cannot be undone.`,
    confirmLabel: 'Delete',
    confirmColor: 'danger' as const,
  };
};

export const ArchiveActionAlert = (props: ArchiveActionAlertProps) => {
  const { action, children, onConfirm, count = 0, placement = 'bottom-start' } = props;

  const config = getArchiveActionConfig(action, count);

  return (
    <Alert
      type={config.type}
      color={config.color}
      icon={config.icon}
      maxWidth={350}
      title={config.type === 'danger' ? <>{config.title}</> : config.title}
      message={config.message}
      secondaryButton={{ children: 'Cancel' }}
      primaryButton={{
        children: config.confirmLabel,
        color: config.confirmColor,
        onClick: onConfirm,
      }}
      popoverProps={{
        offset: [0, placement === 'bottom-start' ? 12 : 8],
        placement,
      }}
      trigger={children}
    />
  );
};

interface ArchiveActionAlertProps {
  action: ArchiveAction;
  count?: number;
  onConfirm: () => Promise<unknown> | void;
  placement?: 'bottom-start' | 'bottom-end';
  children: ReactElement;
}
